/**
 * Consciousness Token Control — client
 *
 * Thin client over the real, server-side budget in
 * lib/consciousness/serverBudget.ts (exposed via /api/consciousness/budget).
 *
 * Previously this class tracked tier and usage entirely in localStorage: tier
 * came from `localStorage.getItem('user_tier')` with no auth check (anyone
 * could set it to the top tier for free), and nothing anywhere in the app
 * ever called the old canMakeRequest()/recordUsage() methods before or after
 * a real provider call — the numbers shown in the UI never reflected real
 * spend. Real enforcement now happens server-side in providerClient.callText.
 * This class exists to give the UI a synchronously-readable, periodically
 * refreshed view of the real budget.
 */

export interface TokenBudget {
  dailyLimit: number;
  monthlyLimit: number;
  currentDailyUsage: number;
  currentMonthlyUsage: number;
  dailyRemaining: number;
  monthlyRemaining: number;
  tier: "free" | "starter" | "pro" | "sovereign" | "god_mode";
  consciousnessMultiplier: number;
}

const DEFAULT_BUDGET: TokenBudget = {
  dailyLimit: 2_000,
  monthlyLimit: 20_000,
  currentDailyUsage: 0,
  currentMonthlyUsage: 0,
  dailyRemaining: 2_000,
  monthlyRemaining: 20_000,
  tier: "free",
  consciousnessMultiplier: 1,
};

export class ConsciousnessTokenControl {
  private budget: TokenBudget = DEFAULT_BUDGET;
  private lastFetchedAt = 0;
  private inFlight: Promise<TokenBudget> | null = null;
  private readonly minRefreshMs = 15_000; // real budget rarely needs sub-15s freshness

  /** Fetch the real budget from the server, deduped and rate-limited. */
  async refresh(force = false): Promise<TokenBudget> {
    const now = Date.now();
    if (!force && now - this.lastFetchedAt < this.minRefreshMs) {
      return this.budget;
    }
    if (this.inFlight) return this.inFlight;

    this.inFlight = (async () => {
      try {
        const res = await fetch("/api/consciousness/budget", { cache: "no-store" });
        if (!res.ok) throw new Error(`budget fetch failed: ${res.status}`);
        const data = await res.json();
        this.budget = {
          dailyLimit: data.dailyLimit,
          monthlyLimit: data.monthlyLimit,
          currentDailyUsage: data.dailyUsage,
          currentMonthlyUsage: data.monthlyUsage,
          dailyRemaining: data.dailyRemaining,
          monthlyRemaining: data.monthlyRemaining,
          tier: data.tier,
          consciousnessMultiplier: 1,
        };
        this.lastFetchedAt = Date.now();
      } catch (error) {
        console.warn("[tokenControl] refresh failed, keeping last known budget:", error);
      } finally {
        this.inFlight = null;
      }
      return this.budget;
    })();

    return this.inFlight;
  }

  /** Synchronous read of the last-fetched budget; call refresh() to update it. */
  getBudgetStatus(): TokenBudget {
    return this.budget;
  }

  getUsageStats(): {
    dailyUsagePercent: number;
    monthlyUsagePercent: number;
    tier: string;
    estimatedCost: number;
  } {
    const b = this.budget;
    const avgCostPerToken = 0.000002; // rough estimate; varies by provider
    return {
      dailyUsagePercent: b.dailyLimit > 0 ? (b.currentDailyUsage / b.dailyLimit) * 100 : 0,
      monthlyUsagePercent: b.monthlyLimit > 0 ? (b.currentMonthlyUsage / b.monthlyLimit) * 100 : 0,
      tier: b.tier,
      estimatedCost: b.currentMonthlyUsage * avgCostPerToken,
    };
  }
}

export const tokenControl = new ConsciousnessTokenControl();
